import { extractFileImportColumn } from '../../features/form/file-import-option-sync.mjs'
import { requireCurrentWorkItemAttemptId } from './work-attempt-policy.mjs'

export const FORM_INPUT_SCHEMA_VERSION = 'work-form-input-1'

export const FORM_HEADER_CATEGORIES = Object.freeze([
  'file_import',
  'workflow_result',
  'direct_input',
  'number',
  'fixed_text',
  'free_input',
  'date',
  'checkbox',
  'radio',
])

const OPTION_HEADER_CATEGORIES = new Set([
  'file_import',
  'workflow_result',
  'direct_input',
])

const HEADER_DEFAULTS = Object.freeze({
  file_import: { colWidth: 240, col_dropdown: [], col_dropdown_origin: [] },
  workflow_result: { colWidth: 240, col_dropdown: [], col_dropdown_origin: [] },
  direct_input: { colWidth: 240, col_dropdown: [], col_dropdown_origin: [] },
  number: { colWidth: 120, number_unit: '' },
  fixed_text: { colWidth: 120, fixed_cell_input_list: [] },
  free_input: { colWidth: 120 },
  date: { colWidth: 160 },
  checkbox: { colWidth: 120 },
  radio: { colWidth: 120 },
})

const UNUSABLE_VERSION_STATUSES = new Set([
  'deleted',
  'discarded',
  'failed',
  'invalid',
])

const defaultIdFactory = () => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
const defaultToday = () => formatWorkFormLocalDate(new Date())

export function formatWorkFormLocalDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function clone(value) {
  if (value === undefined) return undefined
  return JSON.parse(JSON.stringify(value))
}

function hasValue(value) {
  return value !== undefined && value !== null && value !== ''
}

function normalizeSourceIndex(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  if (typeof value === 'string' && !value.trim()) return null
  const normalized = Number(value)
  return Number.isInteger(normalized) && normalized >= 0 ? normalized : null
}

function getSelectedMultiTableDataList(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.selectedMultiTableDataList)) return value.selectedMultiTableDataList
  if (Array.isArray(value?.metadata?.selectedMultiTableDataList)) {
    return value.metadata.selectedMultiTableDataList
  }
  return null
}

function getRawSection(value) {
  return getSelectedMultiTableDataList(value)?.[0]?._document_detail_sections?.[0] ?? null
}

function resolveToday(today) {
  if (typeof today === 'function') return today()
  return today || defaultToday()
}

function defaultCellValue(category, today) {
  if (category === 'date') return today
  if (category === 'checkbox' || category === 'radio') return false
  return null
}

function createCell(header, today, existingCell = null) {
  const cell = existingCell && typeof existingCell === 'object' ? clone(existingCell) : {}
  return {
    ...cell,
    header_id: header.id,
    value: Object.prototype.hasOwnProperty.call(cell, 'value')
      ? cell.value
      : defaultCellValue(header.header_category, today),
    showMenu: false,
  }
}

function normalizeHeader(header = {}) {
  const category = header.header_category
  const defaults = HEADER_DEFAULTS[category] ?? { colWidth: 120 }
  const normalized = {
    ...clone(defaults),
    ...clone(header),
  }

  normalized.colWidth = Number.isFinite(Number(normalized.colWidth))
    ? Number(normalized.colWidth)
    : defaults.colWidth

  if (OPTION_HEADER_CATEGORIES.has(category)) {
    const options = Array.isArray(header.col_dropdown_origin)
      ? clone(header.col_dropdown_origin)
      : Array.isArray(header.col_dropdown)
        ? clone(header.col_dropdown)
        : []
    normalized.col_dropdown = options
    normalized.col_dropdown_origin = options
  }
  if (category === 'fixed_text') {
    normalized.fixed_cell_input_list = Array.isArray(normalized.fixed_cell_input_list)
      ? clone(normalized.fixed_cell_input_list)
      : []
  }
  return normalized
}

export function createEmptyWorkFormInput({ idFactory = defaultIdFactory } = {}) {
  return {
    selectedMultiTableDataList: [{
      _document_detail_sections: [{
        id: idFactory(),
        selectedRowIdList: [],
        canAddTextEngine: false,
        textEngineKeyInput: '',
        section_headers: [],
        section_rows: [],
      }],
    }],
  }
}

export function getWorkFormSection(value = {}) {
  return value?.selectedMultiTableDataList?.[0]?._document_detail_sections?.[0] ?? null
}

export function normalizeWorkFormInput(value, {
  idFactory = defaultIdFactory,
  today,
} = {}) {
  const sourceList = getSelectedMultiTableDataList(value)
  if (!sourceList?.length || !getRawSection(value)) {
    return createEmptyWorkFormInput({ idFactory })
  }

  const normalizedList = clone(sourceList)
  const sourceSection = normalizedList[0]._document_detail_sections[0]
  const headers = Array.isArray(sourceSection.section_headers)
    ? sourceSection.section_headers.map(normalizeHeader)
    : []
  const normalizedToday = resolveToday(today)
  const rows = Array.isArray(sourceSection.section_rows) ? sourceSection.section_rows : []

  const normalizedRows = rows.map(row => {
    const sourceCells = Array.isArray(row?.cells) ? row.cells : []
    const cellsByHeaderId = new Map(sourceCells.map(cell => [cell?.header_id, cell]))
    return {
      ...(row && typeof row === 'object' ? clone(row) : {}),
      id: hasValue(row?.id) ? row.id : idFactory(),
      cells: headers.map(header => createCell(header, normalizedToday, cellsByHeaderId.get(header.id))),
    }
  })

  const selectedRowIds = new Set(normalizedRows.map(row => row.id))
  const selectedRowIdList = Array.isArray(sourceSection.selectedRowIdList)
    ? [...new Set(sourceSection.selectedRowIdList.filter(id => selectedRowIds.has(id)))]
    : []

  normalizedList[0]._document_detail_sections[0] = {
    ...sourceSection,
    id: hasValue(sourceSection.id) ? sourceSection.id : idFactory(),
    selectedRowIdList,
    canAddTextEngine: sourceSection.canAddTextEngine === true,
    textEngineKeyInput: typeof sourceSection.textEngineKeyInput === 'string'
      ? sourceSection.textEngineKeyInput
      : '',
    section_headers: headers,
    section_rows: normalizedRows,
  }

  return { selectedMultiTableDataList: normalizedList }
}

export function addWorkFormColumn(value, definition, {
  idFactory = defaultIdFactory,
  today,
} = {}) {
  const category = definition?.header_category
  if (!FORM_HEADER_CATEGORIES.includes(category)) {
    throw new TypeError(`Unsupported work form header category: ${category}`)
  }

  const normalized = normalizeWorkFormInput(value, { idFactory, today })
  const section = getWorkFormSection(normalized)
  const header = normalizeHeader({
    id: hasValue(definition.id) ? definition.id : idFactory(),
    ...clone(definition),
  })
  const normalizedToday = resolveToday(today)

  section.section_headers.push(header)
  if (section.section_rows.length === 0) {
    section.section_rows.push({
      id: idFactory(),
      cells: [createCell(header, normalizedToday)],
    })
  } else {
    section.section_rows = section.section_rows.map(row => ({
      ...row,
      cells: [...row.cells, createCell(header, normalizedToday)],
    }))
  }
  return normalized
}

export function updateWorkFormColumn(value, headerId, definition = {}) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const headerIndex = section.section_headers.findIndex(header => header.id === headerId)
  if (headerIndex === -1) return normalized

  const previous = section.section_headers[headerIndex]
  const nextCategory = definition.header_category ?? previous.header_category
  if (!FORM_HEADER_CATEGORIES.includes(nextCategory)) {
    throw new TypeError(`Unsupported work form header category: ${nextCategory}`)
  }

  const updated = normalizeHeader({
    ...previous,
    ...clone(definition),
    id: previous.id,
    header_category: nextCategory,
  })
  section.section_headers.splice(headerIndex, 1, updated)

  if (previous.header_category !== nextCategory) {
    const today = defaultToday()
    section.section_rows = section.section_rows.map(row => ({
      ...row,
      cells: row.cells.map(cell => cell.header_id === headerId
        ? createCell(updated, today)
        : cell),
    }))
  }
  return normalized
}

export function removeWorkFormColumn(value, headerId) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  section.section_headers = section.section_headers.filter(header => header.id !== headerId)
  section.section_rows = section.section_rows.map(row => ({
    ...row,
    cells: row.cells.filter(cell => cell.header_id !== headerId),
  }))
  return normalized
}

export function reorderWorkFormColumns(value, orderedHeaderIds = []) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const headersById = new Map(section.section_headers.map(header => [header.id, header]))
  const uniqueOrderedIds = [...new Set(orderedHeaderIds)].filter(id => headersById.has(id))
  const remainingIds = section.section_headers
    .map(header => header.id)
    .filter(id => !uniqueOrderedIds.includes(id))
  const finalIds = [...uniqueOrderedIds, ...remainingIds]

  section.section_headers = finalIds.map(id => headersById.get(id))
  section.section_rows = section.section_rows.map(row => {
    const cellsByHeaderId = new Map(row.cells.map(cell => [cell.header_id, cell]))
    return {
      ...row,
      cells: finalIds.map(id => cellsByHeaderId.get(id)),
    }
  })
  return normalized
}

export function addWorkFormRow(value, {
  idFactory = defaultIdFactory,
  today,
} = {}) {
  const normalized = normalizeWorkFormInput(value, { idFactory, today })
  const section = getWorkFormSection(normalized)
  const normalizedToday = resolveToday(today)
  section.section_rows.push({
    id: idFactory(),
    cells: section.section_headers.map(header => createCell(header, normalizedToday)),
  })
  return normalized
}

export function removeWorkFormRows(value, rowIds = []) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const removedIds = new Set(rowIds)
  section.section_rows = section.section_rows.filter(row => !removedIds.has(row.id))
  section.selectedRowIdList = []
  return normalized
}

export function setWorkFormCellValue(value, rowId, headerId, cellValue) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const header = section.section_headers.find(candidate => candidate.id === headerId)
  if (!header) return normalized

  section.section_rows = section.section_rows.map(row => ({
    ...row,
    cells: row.cells.map(cell => {
      if (cell.header_id !== headerId) return cell
      if (header.header_category === 'radio' && cellValue === true) {
        return { ...cell, value: row.id === rowId }
      }
      return row.id === rowId ? { ...cell, value: cellValue } : cell
    }),
  }))
  return normalized
}

export function selectWorkFormOption(value, rowId, headerId, option = {}) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const selectedHeader = section.section_headers.find(header => header.id === headerId)
  if (!selectedHeader || !Object.prototype.hasOwnProperty.call(option, 'input')) return normalized

  const linkedCategories = ['file_import', 'workflow_result']
  const matchingId = selectedHeader.isMatchingColumnInForm_id
  const canSynchronize = linkedCategories.includes(selectedHeader.header_category)
    && hasValue(matchingId)
    && hasValue(option.id)
  if (!canSynchronize) {
    return setWorkFormCellValue(normalized, rowId, headerId, option.input)
  }

  const matchingHeadersById = new Map(section.section_headers
    .filter(header => (
      linkedCategories.includes(header.header_category)
      && header.isMatchingColumnInForm_id === matchingId
    ))
    .map(header => [header.id, header]))

  section.section_rows = section.section_rows.map(row => {
    if (row.id !== rowId) return row
    return {
      ...row,
      cells: row.cells.map(cell => {
        if (cell.header_id === headerId) return { ...cell, value: option.input }
        const matchingHeader = matchingHeadersById.get(cell.header_id)
        if (!matchingHeader) return cell
        const matchingOption = matchingHeader.col_dropdown_origin
          ?.find(candidate => Object.is(candidate?.id, option.id))
        return matchingOption
          ? { ...cell, value: matchingOption.input }
          : cell
      }),
    }
  })
  return normalized
}

export function setWorkFormSelectedRows(value, rowIds = []) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const availableIds = new Set(section.section_rows.map(row => row.id))
  section.selectedRowIdList = [...new Set(rowIds.filter(id => availableIds.has(id)))]
  return normalized
}

export function buildWorkFormImportedColumns(previewSelectedDataList, sourceType, {
  idFactory = defaultIdFactory,
} = {}) {
  if (!['file_import', 'workflow_result'].includes(sourceType)) {
    throw new TypeError('파일 또는 워크플로우 결과 열 유형을 선택해 주세요.')
  }
  if (!Array.isArray(previewSelectedDataList) || previewSelectedDataList.length !== 1) {
    throw new Error('하나의 파일을 선택해 주세요.')
  }

  const source = previewSelectedDataList[0]
  const tableIndex = normalizeSourceIndex(source?.currTableIdx)
  const selectedColumnValues = Array.isArray(source?.columnSelectList1)
    ? source.columnSelectList1
    : []
  if (tableIndex === null) {
    throw new Error('가져올 시트를 선택해 주세요.')
  }
  if (!selectedColumnValues.length) {
    throw new Error('열을 한 개 이상 선택해 주세요.')
  }
  const selectedColumns = selectedColumnValues.map(normalizeSourceIndex)
  if (selectedColumns.some(columnIndex => columnIndex === null)) {
    throw new Error('선택한 열 정보를 확인해 주세요.')
  }

  const tableData = source?.table_datas?.[tableIndex]?.table_data
  if (!Array.isArray(tableData) || !tableData.length) {
    throw new Error('선택한 파일의 데이터를 불러오지 못했습니다.')
  }

  const matchingId = idFactory()
  return selectedColumns.map(columnIndex => {
    const extracted = extractFileImportColumn(tableData, columnIndex)
    if (!extracted.ok) {
      throw new Error('선택한 열 정보를 확인해 주세요.')
    }
    const options = clone(extracted.options)
    const definition = {
      header_category: sourceType,
      header_name: String(extracted.headerName ?? ''),
      colWidth: 240,
      col_dropdown: options,
      col_dropdown_origin: options,
      file_import_data_id: source.id,
      file_import_data_currTableIdx: tableIndex,
      file_import_data_select_col_idx: columnIndex,
      file_import_data_columnSelectList1: selectedColumns.map(Number),
      isMatchingColumnInForm_id: matchingId,
      addFormDropdownItemDialog_input: '',
    }
    if (sourceType === 'workflow_result') {
      definition.file_import_data_rpa_id = source.rpa_id
      definition.file_import_data_output_activity_id = source.output_activity_id
    }
    return definition
  })
}

export function addWorkFormOptionRow(value, {
  headerId,
  rowId,
  valuesByHeaderId = {},
  optionId = defaultIdFactory(),
} = {}) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const selectedHeader = section.section_headers.find(header => header.id === headerId)
  if (!selectedHeader || !OPTION_HEADER_CATEGORIES.has(selectedHeader.header_category)) {
    throw new Error('옵션 열을 찾을 수 없습니다.')
  }

  const matchingHeaders = selectedHeader.header_category === 'direct_input'
    ? [selectedHeader]
    : section.section_headers.filter(header => (
      ['file_import', 'workflow_result'].includes(header.header_category)
      && header.isMatchingColumnInForm_id === selectedHeader.isMatchingColumnInForm_id
    ))

  matchingHeaders.forEach(header => {
    const input = Object.prototype.hasOwnProperty.call(valuesByHeaderId, header.id)
      ? valuesByHeaderId[header.id]
      : valuesByHeaderId[String(header.id)]
    if (input === undefined) {
      throw new Error('함께 가져온 모든 열의 옵션 값을 입력해 주세요.')
    }
    const options = [...header.col_dropdown_origin, { id: optionId, input }]
    header.col_dropdown_origin = options
    header.col_dropdown = options
  })

  const matchingIds = new Set(matchingHeaders.map(header => header.id))
  section.section_rows = section.section_rows.map(row => {
    if (row.id !== rowId) return row
    return {
      ...row,
      cells: row.cells.map(cell => matchingIds.has(cell.header_id)
        ? {
          ...cell,
          value: Object.prototype.hasOwnProperty.call(valuesByHeaderId, cell.header_id)
            ? valuesByHeaderId[cell.header_id]
            : valuesByHeaderId[String(cell.header_id)],
        }
        : cell),
    }
  })
  return normalized
}

export function buildWorkFormSourceRowPayload(value, headerId, rowId) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  const selectedHeader = section.section_headers.find(header => header.id === headerId)
  if (!selectedHeader || selectedHeader.header_category === 'direct_input') return null
  if (!['file_import', 'workflow_result'].includes(selectedHeader.header_category)) return null

  const matchingHeaders = section.section_headers.filter(header => (
    ['file_import', 'workflow_result'].includes(header.header_category)
    && header.isMatchingColumnInForm_id === selectedHeader.isMatchingColumnInForm_id
  ))
  const row = section.section_rows.find(candidate => candidate.id === rowId)
  if (!matchingHeaders.length || !row) return null
  const cellValueByHeaderId = new Map(row.cells.map(cell => [cell.header_id, cell.value]))

  return {
    id: selectedHeader.file_import_data_id,
    table_idx: selectedHeader.file_import_data_currTableIdx,
    column_idxs: matchingHeaders.map(header => header.file_import_data_select_col_idx),
    datas: matchingHeaders.map(header => cellValueByHeaderId.get(header.id)),
  }
}

export function buildWorkFormTextEnginePayload(value, textItems = []) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  if (section.textEngineKeyInput !== '영실업') {
    throw new Error('현재 지원하는 텍스트 엔진 Key는 영실업입니다.')
  }

  const sourceHeaders = section.section_headers.filter(header => (
    ['file_import', 'workflow_result'].includes(header.header_category)
  ))
  if (sourceHeaders.length < 4) {
    throw new Error('텍스트 엔진을 사용하려면 파일 또는 워크플로우 옵션 열이 네 개 이상 필요합니다.')
  }

  const text = textItems
    .filter(item => typeof item === 'string' || item?.is_abled !== false)
    .map(item => typeof item === 'string' ? item : item?.text)
    .filter(item => typeof item === 'string' && item.trim())
  if (!text.length) {
    throw new Error('텍스트 엔진에 전달할 문장을 한 개 이상 입력해 주세요.')
  }

  const buildKeyInfo = headers => ({
    mtd_id: headers[0].file_import_data_id,
    table_idx: headers[0].file_import_data_currTableIdx,
    col_idxs: headers.map(header => header.file_import_data_select_col_idx),
  })
  return {
    engine_type: 'youngtoys_kakao',
    text,
    key_info: [
      buildKeyInfo(sourceHeaders.slice(0, 2)),
      buildKeyInfo(sourceHeaders.slice(2, 4)),
    ],
  }
}

function isTextEngineTypeCompatible(headerCategory, engineType) {
  return headerCategory === engineType
    || (headerCategory === 'workflow_result' && engineType === 'file_import')
}

function normalizeCandidateOptions(candidates, value, header, idFactory) {
  const candidateValues = [value, ...(Array.isArray(candidates) ? candidates : [])]
    .map(candidate => typeof candidate === 'object' ? candidate?.input : candidate)
  const uniqueValues = [...new Set(candidateValues)]
  return uniqueValues.map(input => {
    const existing = header.col_dropdown_origin?.find(option => Object.is(option.input, input))
    return { id: existing?.id ?? idFactory(), input }
  })
}

export function applyWorkFormTextEngineRows(value, engineRows, {
  idFactory = defaultIdFactory,
} = {}) {
  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  if (!Array.isArray(engineRows)) {
    throw new Error('텍스트 엔진 응답의 행 구조를 확인해 주세요.')
  }

  const newRows = engineRows.map(engineRow => {
    if (!Array.isArray(engineRow) || engineRow.length !== section.section_headers.length) {
      throw new Error('현재 Form의 열 개수와 텍스트 엔진 결과의 열 개수가 다릅니다.')
    }
    const rowId = idFactory()
    const cells = engineRow.map((engineCell, index) => {
      const header = section.section_headers[index]
      if (!isTextEngineTypeCompatible(header.header_category, engineCell?.type)) {
        throw new Error('현재 Form의 열 유형과 텍스트 엔진 결과의 열 유형이 다릅니다.')
      }
      let cellValue = engineCell?.value ?? null
      if (
        section.textEngineKeyInput === '영실업'
        && index === engineRow.length - 1
        && engineCell?.type === 'free_input'
        && !cellValue
      ) {
        cellValue = '박스'
      }
      const cell = {
        ...clone(engineCell),
        header_id: header.id,
        value: cellValue,
        showMenu: false,
      }
      if (Array.isArray(engineCell?.candidates_arr)) {
        cell.candidates_arr = normalizeCandidateOptions(
          engineCell.candidates_arr,
          cellValue,
          header,
          idFactory,
        )
      }
      return cell
    })
    return { id: rowId, cells }
  })

  section.section_rows.push(...newRows)
  return normalized
}

export function validateWorkFormInput(value) {
  const section = getRawSection(value)
  if (!section || !Array.isArray(section.section_headers) || !Array.isArray(section.section_rows)) {
    return {
      valid: false,
      errors: ['폼 입력 구조를 확인해 주세요.'],
    }
  }

  const headers = section.section_headers
  if (headers.length === 0) {
    return {
      valid: false,
      errors: ['열을 한 개 이상 추가해 주세요.'],
    }
  }

  const errors = []
  const headerIds = headers.map(header => header?.id)
  if (headers.some(header => !hasValue(header?.id))) {
    errors.push('모든 열에 ID가 필요합니다.')
  }
  if (headers.some(header => typeof header?.header_name !== 'string' || !header.header_name.trim())) {
    errors.push('모든 열의 이름을 입력해 주세요.')
  }
  if (new Set(headerIds).size !== headerIds.length) {
    errors.push('열 ID가 중복되었습니다.')
  }
  if (headers.some(header => !FORM_HEADER_CATEGORIES.includes(header?.header_category))) {
    errors.push('지원하지 않는 열 유형이 있습니다.')
  }
  if (headers.some(header => {
    if (!OPTION_HEADER_CATEGORIES.has(header?.header_category)) return false
    if (!Array.isArray(header.col_dropdown) || !Array.isArray(header.col_dropdown_origin)) return true
    return [header.col_dropdown, header.col_dropdown_origin].some(options => {
      const optionIds = options.map(option => option?.id)
      return new Set(optionIds).size !== optionIds.length
    })
  })) {
    errors.push('옵션 ID가 중복된 열이 있습니다.')
  }

  const rowsInvalid = section.section_rows.some(row => {
    if (!Array.isArray(row?.cells) || row.cells.length !== headers.length) return true
    const cellHeaderIds = row.cells.map(cell => cell?.header_id)
    return new Set(cellHeaderIds).size !== cellHeaderIds.length
      || cellHeaderIds.some((id, index) => id !== headerIds[index])
  })
  if (rowsInvalid) {
    errors.push('행의 셀 구성이 열과 일치하지 않습니다.')
  }

  return { valid: errors.length === 0, errors }
}

export function serializeWorkFormInput(value) {
  const validation = validateWorkFormInput(value)
  if (!validation.valid) {
    const error = new Error(validation.errors.join('\n'))
    error.name = 'WorkFormInputValidationError'
    error.messages = validation.errors
    throw error
  }

  const normalized = normalizeWorkFormInput(value)
  const section = getWorkFormSection(normalized)
  section.selectedRowIdList = []
  section.section_rows = section.section_rows.map(row => ({
    ...row,
    cells: row.cells.map(cell => ({ ...cell, showMenu: false })),
  }))
  section.section_headers = section.section_headers.map(header => {
    if (header.header_category !== 'fixed_text') return header
    return {
      ...header,
      fixed_cell_input_list: section.section_rows.map(row => (
        row.cells.find(cell => cell.header_id === header.id)?.value ?? null
      )),
    }
  })

  return {
    schema_version: FORM_INPUT_SCHEMA_VERSION,
    selectedMultiTableDataList: clone(normalized.selectedMultiTableDataList),
  }
}

function hasFormSection(version) {
  const section = version?.metadata?.selectedMultiTableDataList?.[0]?._document_detail_sections?.[0]
  return section && Array.isArray(section.section_headers) && Array.isArray(section.section_rows)
}

function getAttemptId(version) {
  return version?.attempt ?? version?.attempt_id ?? null
}

function versionTimestamp(version) {
  const timestamp = Date.parse(version?.created_at ?? '')
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export function getLatestWorkFormInputVersion(versions, currentAttemptId) {
  if (!Array.isArray(versions)) return null
  const usable = versions.filter(version => (
    version?.input_type === 'form'
    && version?.is_available !== false
    && version?.is_deleted !== true
    && !UNUSABLE_VERSION_STATUSES.has(String(version?.status ?? '').toLowerCase())
    && hasFormSection(version)
  ))
  if (!usable.length) return null

  return [...usable].sort((left, right) => {
    if (hasValue(currentAttemptId)) {
      const leftCurrent = String(getAttemptId(left)) === String(currentAttemptId)
      const rightCurrent = String(getAttemptId(right)) === String(currentAttemptId)
      if (leftCurrent !== rightCurrent) return leftCurrent ? -1 : 1
    }
    const versionDifference = Number(right?.version_no ?? 0) - Number(left?.version_no ?? 0)
    if (versionDifference) return versionDifference
    return versionTimestamp(right) - versionTimestamp(left)
  })[0]
}

export function resolveWorkFormInputDraft({
  versions = [],
  currentAttemptId = null,
  inputConfig = {},
  metadata = {},
} = {}) {
  const latest = getLatestWorkFormInputVersion(versions, currentAttemptId)
  if (latest) return normalizeWorkFormInput(latest.metadata)
  const initial = inputConfig?.initial_form_data || metadata?.input_config?.initial_form_data
  return initial ? normalizeWorkFormInput(initial) : createEmptyWorkFormInput()
}

export function buildWorkFormInputPayload({ item = {}, value }) {
  return {
    attempt_id: requireCurrentWorkItemAttemptId(item),
    input_type: 'form',
    display_name: `${item.name || '할일'} 폼 입력`,
    metadata: serializeWorkFormInput(value),
  }
}
